import React, { useState, useRef } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import api from '../utils/api';
import { compressImage } from '../utils/imageCompression';
import { useDialog } from '../hooks/useDialog';
import toast from 'react-hot-toast';

const PhotoUpload = ({ onClose, onUploaded }) => {
  const dialogRef = useDialog(onClose);
  const { sessionId } = useParams();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (selected.size > 5 * 1024 * 1024) {
      toast.error('Photo must be under 5MB');
      return;
    }

    setFile(selected);
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target.result);
    reader.readAsDataURL(selected);
  };

  const handleUpload = async () => {
    if (!file || !sessionId) return;
    setUploading(true);
    setProgress(10);

    try {
      // Compress
      setProgress(30);
      const compressed = await compressImage(file);

      // Build form data
      const formData = new FormData();
      formData.append('photo', compressed, 'photo.jpg');
      if (caption.trim()) {
        formData.append('caption', caption.trim());
      }

      setProgress(50);
      const response = await api.post(
        `/api/sessions/${sessionId}/photos`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) {
              setProgress(50 + Math.round((e.loaded / e.total) * 45));
            }
          }
        }
      );

      setProgress(100);
      toast.success('Photo shared!');
      if (onUploaded) onUploaded(response.data.photo);
      onClose();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.error || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Share a photo"
    >
      <div
        ref={dialogRef}
        className="bg-gray-800 rounded-xl max-w-md w-full p-4 sm:p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Share Photo</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close photo dialog">
            <X className="w-5 h-5" />
          </button>
        </div>

        {!preview ? (
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full border-2 border-dashed border-gray-600 rounded-lg py-12 flex flex-col items-center gap-3 text-gray-400 hover:border-purple-500 hover:text-purple-400 transition-colors"
          >
            <ImageIcon className="w-10 h-10" />
            <span className="text-sm">Tap to select a photo</span>
          </button>
        ) : (
          <div className="relative">
            <img src={preview} alt="Preview" className="w-full rounded-lg max-h-64 object-cover" />
            <button
              onClick={() => { setFile(null); setPreview(null); }}
              className="absolute top-2 right-2 bg-black/60 rounded-full p-2 text-white hover:bg-black/80"
              aria-label="Remove selected photo"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFileSelect}
        />

        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 100))}
          placeholder="Add a caption (optional)"
          className="w-full bg-gray-700 text-white rounded-lg px-4 py-2 text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />

        {uploading && (
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div
              className="bg-purple-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium py-2.5 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          <Upload className="w-4 h-4" />
          {uploading ? 'Uploading...' : 'Share Photo'}
        </button>

        <p className="text-xs text-gray-400 text-center">
          Photos disappear 24 hours after the party — save the ones you want to keep
        </p>
      </div>
    </div>
  );
};

export default PhotoUpload;
